'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PresetsPanel from '@/components/PresetsPanel';
import { usePresets } from '@/lib/usePresets';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Web Audio API pitch shift for preview only (resampling approach — fast, no duration preservation)
function pitchShiftPreview(buffer: AudioBuffer, semitones: number): AudioBuffer {
  const rate = Math.pow(2, semitones / 12);
  const newLength = Math.round(buffer.length / rate);
  const ctx = new OfflineAudioContext(buffer.numberOfChannels, newLength, buffer.sampleRate);
  // We build a raw resampled buffer for preview
  const newBuffer = new AudioBuffer({
    numberOfChannels: buffer.numberOfChannels,
    length: newLength,
    sampleRate: buffer.sampleRate,
  });
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = newBuffer.getChannelData(ch);
    for (let i = 0; i < newLength; i++) {
      const srcIdx = i * rate;
      const idx = Math.floor(srcIdx);
      const frac = srcIdx - idx;
      const a = input[idx] ?? 0;
      const b = input[Math.min(idx + 1, input.length - 1)] ?? 0;
      output[i] = a + frac * (b - a);
    }
  }
  void ctx;
  return newBuffer;
}

function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2;
  const ab = new ArrayBuffer(44 + length);
  const view = new DataView(ab);
  const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); view.setUint32(4, 36 + length, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true); view.setUint16(34, 16, true); ws(36, 'data');
  view.setUint32(40, length, true);
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

const SEMITONE_PRESETS = [-12, -7, -5, -3, -1, 0, 1, 3, 5, 7, 12];

interface PitchPresetSettings {
  semitones: number;
  outputFormat: 'mp3' | 'wav';
}

export default function PitchChangerTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number } | null>(null);
  const [semitones, setSemitones] = useState(0);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');

  // Preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Export state
  const [processing, setProcessing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { presets, savePreset, deletePreset } = usePresets<PitchPresetSettings>('pitch-changer');

  const getCurrentSettings = (): PitchPresetSettings => ({ semitones, outputFormat });

  const applyPresetSettings = (settings: PitchPresetSettings) => {
    setSemitones(settings.semitones);
    setOutputFormat(settings.outputFormat);
    setResultUrl(null);
    setPreviewUrl(null);
  };

  // Clear preview when semitones change
  useEffect(() => {
    setPreviewUrl(null);
    setResultUrl(null);
  }, [semitones]);

  const loadFile = useCallback((file: File) => {
    const allowedExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm', 'flac'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!allowedExts.includes(ext)) {
      setError('Unsupported file type. Please upload MP3, WAV, OGG, M4A, AAC, WebM, or FLAC.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('File too large. Maximum supported size is 100 MB.');
      return;
    }
    setError(null);
    setResultUrl(null);
    setPreviewUrl(null);
    setSemitones(0);
    setAudioFile({ file, url: URL.createObjectURL(file), size: file.size });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const handleReset = () => {
    setAudioFile(null);
    setSemitones(0);
    setPreviewUrl(null);
    setResultUrl(null);
    setResultSize(0);
    setError(null);
    setProgressPct(0);
    setProgressLabel('');
  };

  // Web Audio API preview — fast, browser-side resampling
  const handlePreview = async () => {
    if (!audioFile || semitones === 0) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
      if (!AudioCtx) throw new Error('Web Audio API not supported.');
      const arrayBuffer = await audioFile.file.arrayBuffer();
      const audioCtx = new AudioCtx();
      const decoded = await audioCtx.decodeAudioData(arrayBuffer);
      await audioCtx.close();
      const shifted = pitchShiftPreview(decoded, semitones);
      const blob = audioBufferToWavBlob(shifted);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error(err);
      setError('Preview failed. Try a WAV or MP3 file.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ffmpeg.wasm export — asetrate shifts pitch, atempo restores duration
  const handleProcess = async () => {
    if (!audioFile || semitones === 0) return;
    setProcessing(true);
    setProgressPct(5);
    setProgressLabel('Loading ffmpeg…');
    setError(null);
    setResultUrl(null);

    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      setProgressPct(15);
      setProgressLabel('Initialising ffmpeg…');

      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgressPct(Math.round(20 + progress * 70));
        setProgressLabel(`Processing… ${Math.round(progress * 100)}%`);
      });

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressPct(22);
      setProgressLabel('Reading file…');

      const inputExt = audioFile.file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
      const inputName = `input.${inputExt}`;
      const outputName = `output.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(audioFile.file));

      setProgressPct(25);
      setProgressLabel('Shifting pitch…');

      // asetrate changes the sample rate (shifts pitch), atempo restores the speed/duration
      const pitchRate = Math.pow(2, semitones / 12);
      const originalSampleRate = 44100; // standard; ffmpeg will use actual file rate
      const newSampleRate = Math.round(originalSampleRate * pitchRate);

      // asetrate=newRate shifts pitch, aresample=originalRate restores sample rate,
      // atempo=1/pitchRate restores duration (keeps original length)
      // atempo only accepts 0.5–2.0, so clamp with chaining if needed
      const tempoFactor = 1 / pitchRate;
      const atempoFilters = buildAtempoChain(tempoFactor);
      const filterStr = [
        `asetrate=${newSampleRate}`,
        `aresample=${originalSampleRate}`,
        ...atempoFilters.map(v => `atempo=${v.toFixed(6)}`),
      ].join(',');

      const ffmpegArgs = ['-i', inputName, '-filter:a', filterStr];

      if (outputFormat === 'mp3') {
        ffmpegArgs.push('-codec:a', 'libmp3lame', '-q:a', '2');
      } else {
        ffmpegArgs.push('-codec:a', 'pcm_s16le');
      }
      ffmpegArgs.push(outputName);

      await ffmpeg.exec(ffmpegArgs);

      setProgressPct(92);
      setProgressLabel('Exporting…');

      const data = await ffmpeg.readFile(outputName);
      const mimeType = outputFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav';
      const blob = new Blob([data], { type: mimeType });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgressPct(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Export failed. Please try a different file or format. Large files may exceed browser memory limits.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const sign = semitones > 0 ? '+' : '';
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_pitch${sign}${semitones}st.${outputFormat}`;
    a.click();
  };

  const absS = Math.abs(semitones);
  const isExtreme = absS >= 8;
  const pitchLabel =
    semitones === 0 ? '0 st — Original' :
    semitones > 0 ? `+${semitones} st — Higher` :
    `${semitones} st — Lower`;

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Zone */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🎼</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">MP3, WAV, OGG, M4A, AAC, WebM, FLAC · Max 100 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* File info */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🎼</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)}</p>
            </div>
            <button
              onClick={handleReset}
              title="Reset — upload a new file"
              className="text-gray-400 hover:text-red-500 p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Original preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original Audio</p>
            <audio src={audioFile.url} controls className="w-full h-10" />
          </div>

          {/* Presets Panel */}
          <PresetsPanel
            presets={presets}
            onSave={(name) => savePreset(name, getCurrentSettings())}
            onLoad={applyPresetSettings}
            onDelete={deletePreset}
            accentColor="amber"
          />

          {/* Pitch slider */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">Pitch Shift</label>
              <span className={`font-bold text-sm px-3 py-1 rounded-lg ${semitones === 0 ? 'bg-gray-100 text-gray-600' : semitones > 0 ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                {pitchLabel}
              </span>
            </div>
            <input
              type="range"
              min={-12}
              max={12}
              step={1}
              value={semitones}
              onChange={(e) => setSemitones(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>−12 (Lower)</span>
              <span>0 (Original)</span>
              <span>+12 (Higher)</span>
            </div>

            {/* Preset buttons */}
            <div className="flex flex-wrap gap-2 pt-1">
              {SEMITONE_PRESETS.map(s => (
                <button
                  key={s}
                  onClick={() => setSemitones(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${semitones === s ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700'}`}
                >
                  {s > 0 ? `+${s}` : s}
                </button>
              ))}
            </div>

            {/* Reset pitch button */}
            {semitones !== 0 && (
              <button
                onClick={() => setSemitones(0)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-amber-600 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Reset to 0 semitones
              </button>
            )}
          </div>

          {/* Extreme pitch warning */}
          {isExtreme && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <svg className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-orange-700 text-sm">
                <span className="font-semibold">Extreme pitch changes may reduce audio quality.</span> Shifts beyond ±7 semitones can introduce artifacts. For best results, stay within ±5 semitones.
              </p>
            </div>
          )}

          {/* Output format */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Export Format</p>
            <div className="flex gap-3">
              {(['mp3', 'wav'] as const).map(fmt => (
                <button
                  key={fmt}
                  onClick={() => setOutputFormat(fmt)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${outputFormat === fmt ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300'}`}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Preview processed (Web Audio API) */}
          {semitones !== 0 && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Processed Preview</p>
                <span className="text-xs text-gray-400">Web Audio API · fast preview</span>
              </div>
              {previewUrl ? (
                <audio src={previewUrl} controls className="w-full h-10" />
              ) : (
                <button
                  onClick={handlePreview}
                  disabled={previewLoading}
                  className="w-full py-2.5 bg-gray-200 hover:bg-gray-300 disabled:opacity-60 text-gray-700 font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {previewLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Generating preview…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
                      </svg>
                      Preview Processed Audio
                    </>
                  )}
                </button>
              )}
              <p className="text-xs text-gray-400">* Preview uses resampling (fast). Export uses ffmpeg for better quality with preserved duration.</p>
            </div>
          )}

          {/* Progress */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">
                  Pitch shifted {semitones > 0 ? `+${semitones}` : semitones} semitones · {formatBytes(resultSize)}
                </p>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download as {outputFormat.toUpperCase()}
              </button>
            </div>
          )}

          {/* Export button */}
          {!resultUrl && (
            <button
              onClick={handleProcess}
              disabled={processing || semitones === 0}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
            >
              {processing
                ? 'Processing…'
                : semitones === 0
                ? 'Select a pitch shift above'
                : `Export with ${semitones > 0 ? '+' : ''}${semitones} Semitones as ${outputFormat.toUpperCase()}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Build atempo filter chain — atempo only accepts 0.5–2.0 per filter
function buildAtempoChain(tempo: number): number[] {
  const filters: number[] = [];
  let remaining = tempo;
  while (remaining > 2.0) {
    filters.push(2.0);
    remaining /= 2.0;
  }
  while (remaining < 0.5) {
    filters.push(0.5);
    remaining /= 0.5;
  }
  filters.push(remaining);
  return filters;
}
